import { flexPos } from '@/assets/styles/mixin';
import { EMessageStatus } from '@/store/messageStore';
import React, { useState, useEffect, ReactNode, FC } from 'react';
import { styled } from 'styled-components';
import useAppStore from '@/store/appStore';
import { Message } from '@arco-design/web-react';
import { useLang } from '@/locales/useLang';

interface IProgressCircle {
  status: EMessageStatus;
  progress: number;
  isMe: boolean;
  children?: ReactNode;
  width?: string;
  height?: string;
  bg?: string;
  className?: string;
  isStopUpload?: boolean;
  onClickMask?: () => void;
  render?: (isLoaded: boolean) => ReactNode;
}

interface IStatusProps {
  onClick?: () => void;
}

const ProgressCircleWrap = styled.div<{ $isMe: boolean }>`
  /* width: 100%; */
  height: 100%;
  position: relative;
  left: 0;
  top: 0;

  .progress-mask {
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    cursor: pointer;
  }

  .progress-content {
    width: 100%;
    height: 100%;
    ${flexPos('flex-end')}
    justify-content: ${(props) => (props.$isMe ? 'flex-end' : 'flex-start')};
    cursor: pointer;
  }
`;

const ProgressCircle: FC<IProgressCircle> = (props) => {
  const loadContent = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const appStore = useAppStore();
  const $t = useLang();

  useEffect(() => {
    if (props.progress >= 100) {
      setTimeout(() => {
        return setIsLoaded(true);
      }, 500);
    }

    return setIsLoaded(false);
  }, [props.progress]);

  const [contentSize, setContentSize] = useState({
    width: 'auto',
    height: 'auto',
    borderRadius: '0px',
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const style = getComputedStyle(loadContent.current!.firstElementChild!);
    setContentSize({
      width: props.width || style.width,
      height: props.height || style.height,
      borderRadius: style.borderRadius,
    });
  }, [loadContent, document.documentElement.style?.fontSize]);

  const isShowMask = useMemo(() => {
    if (props.status === EMessageStatus.fail) {
      return true;
    }

    if (props.status === EMessageStatus.pending) {
      return !isLoaded;
    }
  }, [props.status, isLoaded]);

  useEffect(() => {
    if (!Number.parseFloat(contentSize.height)) {
      return;
    }
    const div = loadContent?.current?.querySelector('div');
    div && (div.style.height = '100%');
  }, [contentSize]);

  return (
    <ProgressCircleWrap
      $isMe={props.isMe}
      className={props.className}
      style={{
        height:
          props.height || (Number.parseFloat(contentSize.height) ? contentSize.height : '100%'),
      }}
    >
      <div className="progress-content" ref={loadContent}>
        {props.render
          ? props.render(props.status === EMessageStatus.success || isLoaded)
          : props.children}
      </div>
      <div
        onClick={() => props.onClickMask?.()}
        className="progress-mask"
        style={{
          display: isShowMask ? 'block' : 'none',
          backgroundColor: props.bg || '#0000000',
          borderRadius: contentSize.borderRadius,
        }}
      ></div>
      {props.status === EMessageStatus.pending && !isLoaded && (
        <Circle
          isPause={props.isStopUpload || false}
          onClick={() => props.onClickMask?.()}
          progress={props.progress}
        />
      )}
      {props.status === EMessageStatus.fail && (
        <Fail
          onClick={() => {
            if (appStore.isKickOff) {
              Message.info($t('chat_session_status_8'));
              return;
            }

            props.onClickMask?.();
          }}
        />
      )}
    </ProgressCircleWrap>
  );
};

export default ProgressCircle;

const CircleWrap = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  ${flexPos('center')}

  .progress-text {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    font-size: 16rem;
    font-weight: bold;
    color: #fff;
  }

  .progress-pause {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    color: #fff;
    width: 23rem;
  }
`;

interface ICircle {
  progress: number;
  isPause: boolean;
  onClick?: () => void;
}

const Circle: FC<ICircle> = ({ progress, onClick, isPause }) => {
  const radius = 15;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <CircleWrap onClick={() => onClick?.()}>
      {isPause ? (
        <svg
          className="progress-pause"
          width="20"
          height="30"
          viewBox="0 0 20 30"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2 9C2.8 8.2 7.66667 4 10 2M10 2L18 9M10 2V12M10 18V20M10 26V28"
            stroke="white"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <div className="progress-text">{progress}%</div>
      )}

      <svg
        className="w-80 h-80"
        width="32"
        height="32"
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_1521_1409)">
          <g filter="url(#filter0_b_1521_1409)">
            <circle cx="16" cy="16" r="16" fill="black" fillOpacity="0.01" />
            <circle cx="16" cy="16" r="15" stroke="white" strokeOpacity="0.2" strokeWidth="2" />
          </g>
          <path
            d="M16 30C23.3957 30 29.452 24.2654 29.9648 17.0001C30.0037 16.4492 30.4477 16 31 16C31.5523 16 32.0032 16.4489 31.9692 17.0002C31.453 25.3708 24.5006 32 16 32C7.16344 32 0 24.8366 0 16C0 7.49936 6.62918 0.547037 14.9998 0.030753C15.5511 -0.00324618 16 0.447715 16 1C16 1.55228 15.5508 1.99629 14.9999 2.03517C7.73465 2.54799 2 8.60429 2 16C2 23.732 8.26801 30 16 30Z"
            fill="url(#paint0_linear_1521_1409)"
          />
          <circle
            cx="16"
            cy="16"
            r="15"
            stroke="white"
            strokeWidth="2"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            transform="rotate(-90 16 16)" //
          />
        </g>
        <defs>
          <filter
            id="filter0_b_1521_1409"
            x="-4"
            y="-4"
            width="40"
            height="40"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feGaussianBlur in="BackgroundImageFix" stdDeviation="2" />
            <feComposite
              in2="SourceAlpha"
              operator="in"
              result="effect1_backgroundBlur_1521_1409"
            />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="effect1_backgroundBlur_1521_1409"
              result="shape"
            />
          </filter>
          <linearGradient
            id="paint0_linear_1521_1409"
            x1="16.5"
            y1="0.5"
            x2="33"
            y2="17.5"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="white" />
            <stop offset="1" stopColor="white" stopOpacity="0" />
          </linearGradient>
          <clipPath id="clip0_1521_1409">
            <rect width="32" height="32" fill="white" />
          </clipPath>
        </defs>
      </svg>
    </CircleWrap>
  );
};

const FailWrap = styled.svg`
  width: 80rem;
  height: 80rem;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 9;
  cursor: pointer;
`;

const Fail: FC<IStatusProps> = (props) => {
  return (
    <FailWrap
      onClick={() => props.onClick?.()}
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_141_853)">
        <g filter="url(#filter0_b_141_853)">
          <circle cx="16" cy="16" r="16" fill="black" fillOpacity="0.01" />
          <circle cx="16" cy="16" r="15.5" stroke="white" />
        </g>
        <path
          d="M16 9V19.5"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="16" cy="23.5" r="1" fill="white" />
      </g>
      <defs>
        <filter
          id="filter0_b_141_853"
          x="-4"
          y="-4"
          width="40"
          height="40"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feGaussianBlur in="BackgroundImageFix" stdDeviation="2" />
          <feComposite in2="SourceAlpha" operator="in" result="effect1_backgroundBlur_141_853" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_backgroundBlur_141_853"
            result="shape"
          />
        </filter>
        <clipPath id="clip0_141_853">
          <rect width="32" height="32" fill="white" />
        </clipPath>
      </defs>
    </FailWrap>
  );
};
